<template>
  <div>
    <b-loading is-full-page :active="loading.general" can-cancel />

    <form @submit.prevent="salvarDados">
      <b-field expanded grouped class="column-direction-touch">
        <b-field expanded label="Unidade de compra">
          <b-select
            expanded
            :value="selectedPurchasingUnitId"
            :loading="purchasingUnitFilterOptionsLoading"
            :disabled="
              purchasingUnitLocked ||
              purchasingUnitFilterOptionsLoading ||
              !!purchasingUnitFilterOptionsError
            "
            @input="selectPurchasingUnit"
          >
            <option :value="null" disabled hidden>Selecione uma unidade de compra</option>
            <option v-for="unit in purchasingUnitFilterOptions" :key="unit.id" :value="unit.id">
              {{ unit.code }} — {{ unit.name }}
            </option>
          </b-select>
          <div v-if="purchasingUnitFilterOptionsError" class="help is-danger">
            Não foi possível carregar as unidades de compra. Tente novamente.
            <button
              type="button"
              class="button is-small is-text"
              :disabled="purchasingUnitFilterOptionsLoading"
              @click="loadEligiblePurchasingUnits"
            >
              Tentar novamente
            </button>
          </div>
          <p v-else-if="purchasingUnitLocked" class="help">
            A unidade de compra não pode ser alterada após a publicação da oferta.
          </p>
        </b-field>
        <b-field
          expanded
          label="Número do Processo"
          :type="campoInvalido('numeroProcesso') ? 'is-danger' : ''"
          :message="
            campoInvalido('numeroProcesso') ? 'Número do Processo é um campo obrigatório' : ''
          "
        >
          <b-input
            :disabled="disableFields()"
            custom-class="uppercase"
            placeholder="Número do Processo"
            v-model="$v.dados.numeroProcesso.$model"
            @blur="evento => converterMaiusculo('numeroProcesso', evento.target.value)"
          />
        </b-field>

        <b-field
          expanded
          :label="`Número ${getLabel()}`"
          :type="campoInvalido('numeroOC') ? 'is-danger' : ''"
          :message="campoInvalido('numeroOC') ? `Número ${getLabel()} é um campo obrigatório` : ''"
        >
          <b-input
            :disabled="disableFields()"
            custom-class="uppercase"
            :placeholder="`Número ${getLabel()}`"
            v-model="$v.dados.numeroOC.$model"
            @blur="evento => converterMaiusculo('numeroOC', evento.target.value)"
          />
        </b-field>
      </b-field>
      <div class="auction-data-grid">
        <b-field
          expanded
          :label="`Tipo de ${getLabel()}`"
          :type="campoInvalido('tipoPregao') ? 'is-danger' : ''"
          :message="campoInvalido('tipoPregao') ? `Selecione um tipo de ${getLabel()}` : ''"
        >
          <b-select
            :disabled="disableFields()"
            expanded
            :placeholder="`Tipo de ${getLabel()}`"
            v-model="$v.dados.tipoPregao.$model"
            @click.native="$v.dados.tipoPregao.$touch()"
          >
            <option v-for="tipoPregao in tiposPregao" :key="tipoPregao.id" :value="tipoPregao.enum">
              {{ tipoPregao.nome }}
            </option>
          </b-select>
        </b-field>

        <b-field
          expanded
          label="Modo de Disputa"
          :type="campoInvalido('modoDisputa') ? 'is-danger' : ''"
          :message="campoInvalido('modoDisputa') ? 'Selecione um modo de Disputa' : ''"
        >
          <b-select
            :disabled="disableFields()"
            expanded
            placeholder="Modo de Disputa"
            v-model="$v.dados.modoDisputa.$model"
            @click.native="$v.dados.modoDisputa.$touch()"
          >
            <option
              v-for="modoDisputa in modosDisputaOptions"
              :key="modoDisputa.id"
              :value="modoDisputa.enum"
            >
              {{ modoDisputa.nome }}
            </option>
          </b-select>
        </b-field>
        <b-field
          expanded
          label="Tipo de Negociação"
          :type="campoInvalido('tipoNegociacaoId') ? 'is-danger' : ''"
          :message="campoInvalido('tipoNegociacaoId') ? 'Selecione um tipo de Negociação' : ''"
        >
          <b-select
            :disabled="disableFields()"
            expanded
            placeholder="Tipo de Negociação"
            v-model="$v.dados.tipoNegociacaoId.$model"
            @click.native="$v.dados.tipoNegociacaoId.$touch()"
            @input.native="event => checkHighestDiscount(event.target.value)"
          >
            <option
              v-for="tipoNegociacao in tiposNegociacao"
              :key="tipoNegociacao.id"
              :value="tipoNegociacao.id"
            >
              {{ tipoNegociacao.nome }}
            </option>
          </b-select>
        </b-field>

        <b-field
          expanded
          class="auction-data-grid__edital"
          label="Tipo de Edital"
          :type="campoInvalido('tipoEdital') ? 'is-danger' : ''"
          :message="campoInvalido('tipoEdital') ? 'Selecione um tipo de Edital' : ''"
        >
          <b-select
            :disabled="disableFields()"
            expanded
            placeholder="Tipo de Edital"
            v-model="$v.dados.tipoEdital.$model"
            @click.native="$v.dados.tipoEdital.$touch()"
            @input="value => changeRegionalPreference(value)"
          >
            <option
              v-for="tipoEdital in tiposEditalFiltrado"
              :key="tipoEdital.id"
              :value="tipoEdital.enum"
            >
              {{ tipoEdital.nome }}
            </option>
          </b-select>
        </b-field>
      </div>
      <b-field
        expanded
        label="Objeto"
        :type="campoInvalido('objeto') ? 'is-danger' : ''"
        :message="campoInvalido('objeto') ? 'Objeto é um campo obrigatório' : ''"
      >
        <b-input
          :disabled="disableFields()"
          custom-class="uppercase"
          placeholder="Objeto"
          v-model="$v.dados.objeto.$model"
          @blur="event => converterMaiusculo('objeto', event.target.value)"
        />
      </b-field>

      <hr />

      <b-field grouped class="column-direction-touch date-time-row">
        <b-field
          expanded
          class="date-time-field"
          label="Data de Publicação"
          :type="campoInvalido('dataPublicacao') ? 'is-danger' : ''"
          :message="
            campoInvalido('dataPublicacao') ? 'Selecione uma data válida para publicação' : ''
          "
        >
          <b-datepicker
            :disabled="disableFields()"
            editable
            placeholder="Data de Publicação"
            icon="calendar-today"
            v-mascara:data
            :max-date="maiorDataPublicacao"
            :min-date="menorDataPublicacao"
            :unselectable-days-of-week="diasNaoSelecionaveis"
            :unselectable-dates="feriadosNacionais"
            v-model="valoresMascara.dataPublicacao"
            @input="valor => atualizarDataPersonalizado('dataPublicacao', valor)"
            onkeypress="return event.charCode >= 48 && event.charCode <= 57"
          />
        </b-field>

        <b-field
          expanded
          class="date-time-field"
          label="Data final de entrega de propostas"
          :type="
            campoInvalido('dataFinalEntregaPropostas') ||
            !$v.dados.dataFinalEntregaPropostas.aberturaNaoAnteriorAoPrazoFinal
              ? 'is-danger'
              : ''
          "
          :message="mensagemErroDataFinalEntregaPropostas"
        >
          <b-datepicker
            :disabled="disableFields()"
            editable
            placeholder="Data final de entrega de propostas"
            icon="calendar-today"
            v-mascara:data
            :min-date="menorDataFinalEntregaPropostas"
            :unselectable-days-of-week="diasNaoSelecionaveis"
            :unselectable-dates="feriadosNacionais"
            v-model="valoresMascara.dataFinalEntregaPropostas"
            @input="valor => atualizarDataPersonalizado('dataFinalEntregaPropostas', valor)"
            onkeypress="return event.charCode >= 48 && event.charCode <= 57"
          />
        </b-field>

        <b-field
          expanded
          class="date-time-field"
          label="Horário final de entrega de propostas"
          :type="
            campoInvalido('horaFinalEntregaPropostas') ||
            !$v.dados.horaFinalEntregaPropostas.aberturaNaoAnteriorAoPrazoFinal
              ? 'is-danger'
              : ''
          "
          :message="mensagemErroHoraFinalEntregaPropostas"
        >
          <b-timepicker
            :disabled="disableFields()"
            editable
            maxlength="5"
            placeholder="00:00"
            icon="clock"
            v-mascara:hora
            v-model="valoresMascara.horaFinalEntregaPropostas"
            @input="valor => atualizarHora('horaFinalEntregaPropostas', valor)"
            onkeypress="return event.charCode >= 48 && event.charCode <= 57"
          />
        </b-field>
      </b-field>

      <b-field grouped class="column-direction-touch date-time-row">
        <b-field
          expanded
          class="date-time-field"
          :type="
            campoInvalido('dataAbertura') || !$v.dados.dataAbertura.aberturaNaoAnteriorAoPrazoFinal
              ? 'is-danger'
              : ''
          "
          :message="mensagemErroDataAbertura"
        >
          <template #label>
            <span>
              Data de Abertura da sessão
              <b-tooltip
                label="A abertura da sessão deve ocorrer em data/horário igual ou posterior ao prazo final de envio de propostas e respeitar o mínimo de 8 dias úteis a partir da publicação."
                position="is-bottom"
                multilined
              >
                <b-icon
                  icon="help-circle"
                  size="is-small"
                  class="has-text-grey is-clickable"
                  style="margin-left: 4px"
                />
              </b-tooltip>
            </span>
          </template>

          <b-datepicker
            :disabled="disableFields()"
            editable
            placeholder="Data de Abertura da sessão"
            icon="calendar-today"
            v-mascara:data
            :min-date="menorDataAbertura"
            :unselectable-days-of-week="diasNaoSelecionaveis"
            :unselectable-dates="feriadosNacionais"
            v-model="valoresMascara.dataAbertura"
            @input="valor => atualizarDataPersonalizado('dataAbertura', valor)"
            onkeypress="return event.charCode >= 48 && event.charCode <= 57"
          />
        </b-field>

        <b-field
          expanded
          class="date-time-field"
          :type="
            horaSemDuracaoDisponivel
              ? 'is-danger'
              : campoInvalido('horaAbertura') ||
                !$v.dados.horaAbertura.aberturaNaoAnteriorAoPrazoFinal
              ? 'is-danger'
              : ''
          "
          :message="mensagemErroHoraAbertura"
        >
          <template #label>
            <span>
              Horário de Abertura da sessão
              <b-tooltip
                label="A abertura da sessão deve ocorrer em data/horário igual ou posterior ao prazo final de envio de propostas e respeitar o mínimo de 8 dias úteis a partir da publicação."
                position="is-bottom"
                multilined
              >
                <b-icon
                  icon="help-circle"
                  size="is-small"
                  class="has-text-grey is-clickable"
                  style="margin-left: 4px"
                />
              </b-tooltip>
            </span>
          </template>

          <b-timepicker
            :disabled="disableFields()"
            editable
            ref="hora"
            maxlength="5"
            placeholder="00:00"
            icon="clock"
            v-mascara:hora
            v-model="valoresMascara.horaAbertura"
            @input="valor => atualizarHora('horaAbertura', valor)"
            onkeypress="return event.charCode >= 48 && event.charCode <= 57"
          />
        </b-field>

        <b-field expanded class="date-time-field" />
      </b-field>

      <hr />

      <!-- plain div: b-field grouped wraps children in field-body and gap never reaches toggles -->
      <div class="column-direction-touch toggles-row">
        <b-field
          class="toggle-field"
          label="Preferência Local/Regional"
          v-if="dados.tipoEdital === 'RESERVADO'"
        >
          <b-switch
            :disabled="disableFields()"
            size="is-medium"
            v-model="dados.preferenciaRegional"
            @input="displayPreferenceWarning()"
            >{{ dados.preferenciaRegional ? 'Sim' : 'Não' }}
          </b-switch>
        </b-field>

        <b-field class="toggle-field" label="Divulgar Referencial">
          <b-switch
            :disabled="disableFields() || dados.tipoNegociacaoId === 3"
            size="is-medium"
            v-model="dados.divulgarReferencial"
            @input="exibirAvisoReferencial()"
            >{{ dados.divulgarReferencial ? 'Sim' : 'Não' }}
          </b-switch>
        </b-field>

        <b-field class="toggle-field" label="Inversão de Fases" v-if="showPhaseInversion">
          <b-switch
            :disabled="disableFields()"
            size="is-medium"
            v-model="dados.phaseInversion"
            @input="exibirAvisoInversaoFases()"
          >
            {{ dados.phaseInversion ? 'Sim' : 'Não' }}
          </b-switch>
        </b-field>

        <b-field
          class="toggle-field"
          label="Manifestação de Intenção de Recurso Consolidada"
          v-if="showManifestacaoIntencaoConsolidada"
        >
          <b-switch
            :disabled="disableFields()"
            size="is-medium"
            v-model="dados.manifestacaoIntencaoConsolidada"
            @input="exibirAvisoManifestacaoIntencaoConsolidada()"
          >
            {{ dados.manifestacaoIntencaoConsolidada ? 'Sim' : 'Não' }}
          </b-switch>
        </b-field>
      </div>

      <b-field grouped class="column-direction-touch lances-row">
        <b-field
          v-if="showDuracaoLances"
          expanded
          label="Duração dos Lances"
          :type="
            noDuracaoDisponivel ? 'is-danger' : campoInvalido('duracaoLances') ? 'is-danger' : ''
          "
          :message="duracaoMensagemErro"
        >
          <b-select
            v-if="!noDuracaoDisponivel"
            :disabled="disableFields() || !dados.horaAbertura"
            expanded
            placeholder="Selecione (6h a 10h)"
            v-model="$v.dados.duracaoLances.$model"
            @click.native="$v.dados.duracaoLances.$touch()"
          >
            <option v-for="opt in duracaoOptions" :key="opt" :value="opt">{{ opt }}h</option>
          </b-select>
          <b-select v-else expanded :disabled="true" placeholder="Sem opções válidas">
            <option disabled>Sem opções (abra até 12:00 para habilitar)</option>
          </b-select>
        </b-field>
      </b-field>

      <hr />

      <b-field grouped class="column-direction-touch">
        <b-field
          label="CEP"
          :type="campoInvalido('enderecoEntregaCEP') ? 'is-danger' : ''"
          :message="campoInvalido('enderecoEntregaCEP') ? 'É necessario preencher o CEP' : ''"
        >
          <b-input
            :disabled="disableFields()"
            placeholder="CEP"
            maxlength="9"
            onkeypress="return event.charCode >= 48 && event.charCode <= 57"
            :loading="pesquisando"
            :has-counter="false"
            @blur="buscarCEP"
            @keyup.native.enter.prevent.stop="buscarCEP"
            v-mascara:cep
            v-model="$v.dados.enderecoEntregaCEP.$model"
          />
        </b-field>

        <b-field
          expanded
          label="Endereço de Entrega"
          :type="campoInvalido('enderecoEntrega') ? 'is-danger' : ''"
          :message="campoInvalido('enderecoEntrega') ? 'Endereço é um campo obrigatório' : ''"
        >
          <b-input
            :disabled="disableFields()"
            placeholder="Endereço"
            v-model="$v.dados.enderecoEntrega.$model"
          />
        </b-field>
      </b-field>

      <b-field grouped class="column-direction-touch">
        <b-field
          label="Número"
          :type="campoInvalido('enderecoEntregaNumero') ? 'is-danger' : ''"
          :message="campoInvalido('enderecoEntregaNumero') ? 'Número é um campo obrigatório' : ''"
        >
          <b-input
            :disabled="disableFields()"
            placeholder="Número"
            v-model="$v.dados.enderecoEntregaNumero.$model"
            @input="$v.dados.enderecoEntregaNumero.$touch()"
          />
        </b-field>

        <b-field label="Complemento">
          <b-input
            :disabled="disableFields()"
            custom-class="uppercase"
            placeholder="Complemento"
            v-model="$v.dados.enderecoEntregaComplemento.$model"
            @blur="evento => converterMaiusculo('enderecoEntregaComplemento', evento.target.value)"
          />
        </b-field>
      </b-field>

      <b-field grouped class="column-direction-touch">
        <b-field
          expanded
          label="Bairro"
          :type="campoInvalido('enderecoEntregaBairro') ? 'is-danger' : ''"
          :message="campoInvalido('enderecoEntregaBairro') ? 'Bairro é um campo obrigatório' : ''"
        >
          <b-input
            :disabled="disableFields()"
            placeholder="Bairro"
            v-model="$v.dados.enderecoEntregaBairro.$model"
          />
        </b-field>

        <b-field
          expanded
          label="Município"
          :type="campoInvalido('enderecoEntregaMunicipio') ? 'is-danger' : ''"
          :message="campoInvalido('enderecoEntregaMunicipio') ? 'Preencha o munícipio' : ''"
        >
          <b-input
            :disabled="disableFields()"
            placeholder="Município"
            v-model="$v.dados.enderecoEntregaMunicipio.$model"
          />
        </b-field>

        <b-field
          expanded
          label="UF"
          :type="campoInvalido('enderecoEntregaUF') ? 'is-danger' : ''"
          :message="campoInvalido('enderecoEntregaUF') ? 'O campo UF deve ser preenchido' : ''"
        >
          <b-select
            :disabled="disableFields()"
            expanded
            placeholder="UF"
            v-model="$v.dados.enderecoEntregaUF.$model"
            @click.native="$v.dados.enderecoEntregaUF.$touch()"
          >
            <option v-for="option in ufOptions" :key="option.id" :value="option.value">
              {{ option.name }}
            </option>
          </b-select>
        </b-field>
      </b-field>

      <hr />

      <b-field
        expanded
        class="column-direction-touch"
        label="Origem dos Recursos"
        :type="campoInvalido('origemRecursos') ? 'is-danger' : ''"
        :message="campoInvalido('origemRecursos') ? 'Origem dos recursos deve ser preenchido' : ''"
      >
        <b-taginput
          :disabled="disableFields()"
          autocomplete
          allow-new
          attached
          open-on-focus
          placeholder="Origem dos Recursos"
          :data="origens"
          v-model="$v.dados.origemRecursos.$model"
        />
      </b-field>

      <b-field grouped class="column-direction-touch">
        <b-field
          expanded
          label="Tipo da Origem dos Recursos"
          :type="campoInvalido('tipoEdital') ? 'is-danger' : ''"
          :message="campoInvalido('tipoEdital') ? 'Selecione um tipo da Origem dos Recursos' : ''"
        >
          <b-select
            :disabled="disableFields()"
            expanded
            placeholder="Tipo da Origem dos Recursos"
            v-model="$v.dados.tipoOrigemRecursos.$model"
            @click.native="$v.dados.tipoOrigemRecursos.$touch()"
          >
            <option
              v-for="tipoOrigemRecursos in tiposOrigemRecursos"
              :key="tipoOrigemRecursos.id"
              :value="tipoOrigemRecursos.enum"
            >
              {{ tipoOrigemRecursos.nome }}
            </option>
          </b-select>
        </b-field>

        <b-field expanded label="Número do Instrumento (Convênio)">
          <b-input
            :disabled="disableFields()"
            placeholder="Número do Instrumento"
            v-model="$v.dados.numeroInstrumento.$model"
          />
        </b-field>

        <b-field
          expanded
          label="Ano do Instrumento (Convênio)"
          :type="campoInvalido('anoInstrumento') ? 'is-danger' : ''"
          :message="campoInvalido('anoInstrumento') ? 'Informe um ano entre 1900 e 2099' : ''"
        >
          <b-input
            :disabled="disableFields()"
            placeholder="Ano do Instrumento"
            v-mascara:valor.integer
            v-model="valoresMascara.anoInstrumento"
            @input.native="evento => atualizarValor('anoInstrumento', evento)"
            onkeypress="return event.charCode >= 48 && event.charCode <= 57"
          />
        </b-field>
      </b-field>

      <b-field class="column-direction-touch">
        <button
          class="button is-primary is-fullwidth save-button"
          type="submit"
          :disabled="disableFields()"
          :class="{ ' is-loading': loading.salvar }"
          @click.prevent.stop="salvarDados"
        >
          Salvar
        </button>
      </b-field>
    </form>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { required, requiredIf, minValue, between } from 'vuelidate/lib/validators';
import Holidays from 'date-holidays';

import campoMixin from '@/mixins/formulario';
import pregaoEditarFields from '@/mixins/pregaoEditarFields';
import { dadosCEP } from '@/resources/utils';
import { ufOptions } from '@/utils/data';
import { getOfertaCompraEnum } from '../../../utils/enumerados';

export default {
  name: 'PregaoEditarDados',
  mixins: [campoMixin, pregaoEditarFields],
  data() {
    const hd = new Holidays('BR');
    const diasNaoSelecionaveis = [0, 6];
    const feriadosNacionais = [];
    const tiposPregao = [
      { id: 1, nome: 'Contratação Imediata', enum: 'IMEDIATO' },
      {
        id: 2,
        nome: 'Registro de Preços',
        enum: 'REGISTRO',
      },
    ];
    const tiposNegociacao = [
      { id: 1, nome: 'Valor Unitário' },
      { id: 2, nome: 'Valor Total' },
      { id: 3, nome: 'Maior Desconto' },
    ];
    const modosDisputa = [
      { id: 1, nome: 'Aberto', enum: 'ABERTO' },
      { id: 2, nome: 'Aberto e Fechado', enum: 'FECHADO' },
      { id: 3, nome: 'Fechado e Aberto', enum: 'FECHADO_ABERTO' },
    ];
    const modosDisputaDispensa = [
      { id: 4, nome: 'Com Disputa', enum: 'COM_DISPUTA' },
      { id: 5, nome: 'Sem Disputa', enum: 'SEM_DISPUTA' },
    ];
    const tiposEdital = [
      { id: 1, nome: 'Participação Ampla', enum: 'AMPLO' },
      {
        id: 2,
        nome: 'Com Quota Reservada para ME/EPP/MEI/Cooperativa Preferência',
        enum: 'AMPLOCOTA',
      },
      {
        id: 3,
        nome: 'Participação Exclusiva de ME/EPP/MEI/Cooperativa Preferência',
        enum: 'RESERVADO',
      },
    ];
    const tiposOrigemRecursos = [
      { id: 1, nome: 'Nacional', enum: 'NACIONAL' },
      { id: 2, nome: 'Internacional', enum: 'INTERNACIONAL' },
    ];
    const origens = ['Transferências Voluntárias da União', 'Própria', 'Outras'];
    const dados = {
      tipo: '',
      numeroOC: '',
      numeroProcesso: null,
      objeto: '',
      origemRecursos: [],
      tipoOrigemRecursos: null,
      numeroInstrumento: null,
      anoInstrumento: null,
      tipoNegociacaoId: null,
      dataPublicacao: null,
      dataFinalEntregaPropostas: null,
      horaFinalEntregaPropostas: null,
      dataAbertura: null,
      horaAbertura: null,
      tipoPregao: null,
      duracaoLances: null,
      modoDisputa: null,
      tipoEdital: null,
      preferenciaRegional: null,
      divulgarReferencial: null,
      phaseInversion: null,
      manifestacaoIntencaoConsolidada: null,
      enderecoEntregaCEP: null,
      enderecoEntrega: null,
      enderecoEntregaNumero: null,
      enderecoEntregaComplemento: null,
      enderecoEntregaBairro: null,
      enderecoEntregaMunicipio: null,
      enderecoEntregaUF: null,
    };
    const valoresMascara = {
      dataPublicacao: null,
      dataFinalEntregaPropostas: null,
      horaFinalEntregaPropostas: null,
      dataAbertura: null,
      horaAbertura: null,
      anoInstrumento: null,
    };

    const loading = {
      general: false,
      salvar: false,
    };

    return {
      diasNaoSelecionaveis,
      tiposPregao,
      tiposNegociacao,
      modosDisputa,
      modosDisputaDispensa,
      tiposEdital,
      tiposOrigemRecursos,
      dados,
      valoresMascara,
      ufOptions,
      origens,
      pesquisando: false,
      feriados: hd
        .getHolidays(new Date().getFullYear())
        .concat(hd.getHolidays(new Date().getFullYear() + 1)),
      feriadosNacionais,
      loading,
      dadosMontados: false,
      selectedPurchasingUnitId: null,
    };
  },
  async beforeRouteLeave(to, from, next) {
    if (this.logged && this.$v.dados.$anyDirty) {
      const sair = await this.$confirmacao({
        message: 'Os dados serão perdidos. Tem certeza que deseja continuar?',
      });
      return next(sair);
    }
    return next();
  },
  validations: {
    dados: {
      tipo: { required: false },
      numeroOC: { required },
      numeroProcesso: {
        required: requiredIf(function validarEmPublicacao() {
          return this.emPublicacao;
        }),
      },
      objeto: {
        required: requiredIf(function validarEmPublicacao() {
          return this.emPublicacao;
        }),
      },
      origemRecursos: {
        required: requiredIf(function validarEmPublicacao() {
          return this.emPublicacao;
        }),
      },
      tipoOrigemRecursos: {
        required: requiredIf(function validarEmPublicacao() {
          return this.emPublicacao;
        }),
      },
      numeroInstrumento: {
        required: false,
      },
      anoInstrumento: {
        required: false,
        between: between(1900, 2099),
      },
      tipoNegociacaoId: {
        required: requiredIf(function validarEmPublicacao() {
          return this.emPublicacao;
        }),
        minValue: minValue(1),
      },
      dataPublicacao: {
        required: requiredIf(function validarEmPublicacao() {
          return this.emPublicacao;
        }),
        valorMaximo() {
          if (this.dados.dataAbertura && this.dados.dataPublicacao > this.maiorDataPublicacao) {
            return false;
          }
          return true;
        },
      },
      dataAbertura: {
        required: requiredIf(function validarEmPublicacao() {
          return this.emPublicacao;
        }),
        valorMinimo() {
          if (this.dados.dataPublicacao && this.dados.dataAbertura < this.menorDataAbertura) {
            return false;
          }
          return true;
        },
        aberturaNaoAnteriorAoPrazoFinal() {
          if (!this.emPublicacao) return true;
          return !this.aberturaAnteriorAoPrazoFinal;
        },
      },
      horaAbertura: {
        required: requiredIf(function validarEmPublicacao() {
          return this.emPublicacao;
        }),
        aberturaNaoAnteriorAoPrazoFinal() {
          if (!this.emPublicacao) return true;
          return !this.aberturaAnteriorAoPrazoFinal;
        },
      },
      dataFinalEntregaPropostas: {
        required: requiredIf(function validarEmPublicacao() {
          return this.emPublicacao;
        }),
        valorMinimo() {
          if (
            this.dados.dataPublicacao &&
            this.dados.dataFinalEntregaPropostas < this.menorDataFinalEntregaPropostas
          ) {
            return false;
          }
          return true;
        },
        aberturaNaoAnteriorAoPrazoFinal() {
          if (!this.emPublicacao) return true;
          return !this.aberturaAnteriorAoPrazoFinal;
        },
      },
      horaFinalEntregaPropostas: {
        required: requiredIf(function validarEmPublicacao() {
          return this.emPublicacao;
        }),
        aberturaNaoAnteriorAoPrazoFinal() {
          if (!this.emPublicacao) return true;
          return !this.aberturaAnteriorAoPrazoFinal;
        },
      },
      tipoPregao: {
        required: requiredIf(function validarEmPublicacao() {
          return this.emPublicacao;
        }),
      },
      modoDisputa: {
        required: requiredIf(function validarEmPublicacao() {
          return this.emPublicacao;
        }),
      },
      duracaoLances: {
        required: requiredIf(function validarEmPublicacao() {
          return this.emPublicacao && this.dados.modoDisputa === 'COM_DISPUTA';
        }),
        between: between(6, 10),
        fimAntesOuIgualDezoito() {
          if (this.dados.modoDisputa !== 'COM_DISPUTA') return true;
          if (!this.dados.horaAbertura || !this.dados.duracaoLances) return true;
          const h = this.dados.horaAbertura.getHours?.() ?? 0;
          const m = this.dados.horaAbertura.getMinutes?.() ?? 0;
          const fim = h + (m > 0 ? 1 : 0) + Number(this.dados.duracaoLances);
          return fim <= 18;
        },
      },
      tipoEdital: {
        required: requiredIf(function validarEmPublicacao() {
          return this.emPublicacao;
        }),
      },
      enderecoEntregaCEP: {
        required: requiredIf(function validarEmPublicacao() {
          return this.emPublicacao;
        }),
      },
      enderecoEntrega: {
        required: requiredIf(function validarEmPublicacao() {
          return this.emPublicacao;
        }),
      },
      enderecoEntregaNumero: {
        required: requiredIf(function validarEmPublicacao() {
          return this.emPublicacao;
        }),
      },
      enderecoEntregaComplemento: { required: false },
      enderecoEntregaBairro: {
        required: requiredIf(function validarEmPublicacao() {
          return this.emPublicacao;
        }),
      },
      enderecoEntregaMunicipio: {
        required: requiredIf(function validarEmPublicacao() {
          return this.emPublicacao;
        }),
      },
      enderecoEntregaUF: {
        required: requiredIf(function validarEmPublicacao() {
          return this.emPublicacao;
        }),
      },
    },
  },
  computed: {
    ...mapGetters({
      getOfertaCompra: 'obterOfertaCompra',
      emPublicacao: 'obterEmPublicacaoPregao',
      logged: 'getLogged',
    }),
    ...mapGetters({
      auction: 'obterOfertaCompra',
      function: 'getFunction',
    }),
    ...mapGetters('purchasingUnits', [
      'purchasingUnitFilterOptions',
      'purchasingUnitFilterOptionsLoading',
      'purchasingUnitFilterOptionsError',
    ]),
    purchasingUnitLocked() {
      const publicadoEm = this.getOfertaCompra?.publicadoEm;
      return publicadoEm !== null && publicadoEm !== undefined;
    },
    horaSemDuracaoDisponivel() {
      if (!this.showDuracaoLances) return false;
      if (!this.dados.horaAbertura) return false;

      const h = this.dados.horaAbertura.getHours?.() ?? 0;
      const m = this.dados.horaAbertura.getMinutes?.() ?? 0;
      const hEfetivo = h + (m > 0 ? 1 : 0);

      return 18 - hEfetivo < 6;
    },
    noDuracaoDisponivel() {
      return this.showDuracaoLances && this.duracaoOptions.length === 0;
    },
    modosDisputaOptions() {
      return this.dados?.tipo === 'DISPENSA' ? this.modosDisputaDispensa : this.modosDisputa;
    },
    showDuracaoLances() {
      return this.dados?.modoDisputa === 'COM_DISPUTA';
    },
    duracaoOptions() {
      const allowedDurationsInHours = [6, 7, 8, 9, 10];
      const base = allowedDurationsInHours;
      if (!this.dados.horaAbertura) return base;
      const h = this.dados.horaAbertura.getHours?.() ?? 0;
      const m = this.dados.horaAbertura.getMinutes?.() ?? 0;
      const hEfetivo = h + (m > 0 ? 1 : 0);
      const maxDur = Math.max(0, 18 - hEfetivo);
      return base.filter(v => v <= maxDur);
    },
    duracaoMensagemErro() {
      if (!this.campoInvalido('duracaoLances')) return '';
      // decide mensagem com base nas regras violadas
      const v = this.$v?.dados?.duracaoLances;
      if (v?.$error) {
        if (v?.required === false) return 'Informe a duração da etapa de lances';
        if (v?.between === false) return 'A duração deve estar entre 6h e 10h';
        if (v?.fimAntesOuIgualDezoito === false) return 'A duração ultrapassa 18:00';
      }
      return 'Selecione uma duração válida';
    },
    prazoFinalEntregaDateTime() {
      if (!this.dados.dataFinalEntregaPropostas || !this.dados.horaFinalEntregaPropostas)
        return null;
      const data = new Date(this.dados.dataFinalEntregaPropostas);
      data.setHours(this.dados.horaFinalEntregaPropostas.getHours());
      data.setMinutes(this.dados.horaFinalEntregaPropostas.getMinutes());
      data.setSeconds(0);
      data.setMilliseconds(0);
      return data;
    },
    aberturaSessaoDateTime() {
      if (!this.dados.dataAbertura || !this.dados.horaAbertura) return null;
      const data = new Date(this.dados.dataAbertura);
      data.setHours(this.dados.horaAbertura.getHours());
      data.setMinutes(this.dados.horaAbertura.getMinutes());
      data.setSeconds(0);
      data.setMilliseconds(0);
      return data;
    },
    aberturaAnteriorAoPrazoFinal() {
      if (!this.prazoFinalEntregaDateTime || !this.aberturaSessaoDateTime) return false;
      return this.aberturaSessaoDateTime.getTime() < this.prazoFinalEntregaDateTime.getTime();
    },
    mensagemErroDataFinalEntregaPropostas() {
      if (!this.$v?.dados?.dataFinalEntregaPropostas?.$dirty) return '';
      if (!this.$v.dados.dataFinalEntregaPropostas.required) {
        return 'Selecione uma data válida para entrega final de propostas';
      }
      if (!this.$v.dados.dataFinalEntregaPropostas.valorMinimo) {
        return 'A Data final de entrega de propostas deve respeitar o prazo mínimo de 8 dias úteis contados a partir da Data de Publicação.';
      }
      if (!this.$v.dados.dataFinalEntregaPropostas.aberturaNaoAnteriorAoPrazoFinal) {
        return 'A Data e o Horário de Abertura da sessão não podem ser anteriores à Data e ao Horário final de entrega de propostas.';
      }
      return '';
    },
    mensagemErroDataAbertura() {
      if (!this.$v?.dados?.dataAbertura?.$dirty) return '';
      if (!this.$v.dados.dataAbertura.required || !this.$v.dados.dataAbertura.valorMinimo) {
        return this.$v.dados.dataAbertura.valorMinimo
          ? 'Selecione uma data válida para abertura'
          : 'A Data de Abertura da sessão deve respeitar o prazo mínimo de 8 dias úteis contados a partir da Data de Publicação.';
      }
      if (!this.$v.dados.dataAbertura.aberturaNaoAnteriorAoPrazoFinal) {
        return 'A Data e o Horário de Abertura da sessão não podem ser anteriores à Data e ao Horário final de entrega de propostas.';
      }
      return '';
    },
    mensagemErroHoraFinalEntregaPropostas() {
      if (!this.$v?.dados?.horaFinalEntregaPropostas?.$dirty) return '';
      if (!this.$v.dados.horaFinalEntregaPropostas.required) {
        return 'Selecione uma hora válida para entrega final de propostas';
      }
      if (!this.$v.dados.horaFinalEntregaPropostas.aberturaNaoAnteriorAoPrazoFinal) {
        return 'A Data e o Horário de Abertura da sessão não podem ser anteriores à Data e ao Horário final de entrega de propostas.';
      }
      return '';
    },
    mensagemErroHoraAbertura() {
      if (!this.$v?.dados?.horaAbertura?.$dirty) return '';
      if (!this.$v.dados.horaAbertura.required) {
        return 'Selecione uma hora válida para abertura';
      }
      if (!this.$v.dados.horaAbertura.aberturaNaoAnteriorAoPrazoFinal) {
        return 'A Data e o Horário de Abertura da sessão não podem ser anteriores à Data e ao Horário final de entrega de propostas.';
      }
      return '';
    },
    horaAbertura: {
      get() {
        return this.dados.horaAbertura;
      },
      set(hora) {
        this.dados.horaAbertura = hora;
        if (this.dados.dataAbertura) {
          this.setHora(hora);
        }
      },
    },
    tiposEditalFiltrado() {
      if (
        (this.auction?.situacao?.id > 1 && this.auction?.tipo_edital !== 'AMPLOCOTA') ||
        this.auction?.tipo === 'DISPENSA'
      ) {
        return this.tiposEdital.filter(item => item.enum !== 'AMPLOCOTA');
      }
      return this.tiposEdital;
    },
    showPhaseInversion() {
      return this.auction && ['PREGAO', 'CONCORRENCIA'].includes(this.auction.tipo);
    },
    showManifestacaoIntencaoConsolidada() {
      return this.auction && ['PREGAO', 'CONCORRENCIA', 'DISPENSA'].includes(this.auction.tipo);
    },
    maiorDesconto() {
      return this.getOfertaCompra.tipo_negociacao.id === 3;
    },
    menorDataPublicacao() {
      if (process.env.VUE_APP_ENABLE_RETROACTIVE_DATE === 'true') {
        return null;
      }
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      return yesterday;
    },
    maiorDataPublicacao() {
      let tetoPorAbertura = null;
      if (this.dados.dataAbertura) {
        const data = new Date(this.dados.dataAbertura);
        let diasUteis = this.dados?.tipo === 'DISPENSA' ? 3 : 7;

        data.setDate(data.getDate() - 1);

        while (diasUteis > 0) {
          const feriado = this.feriadosNacionais.find(
            dataFeriado => dataFeriado.toDateString() === data.toDateString(),
          );
          const dia = data.getDay();
          if (dia === 0) {
            data.setDate(data.getDate() - 2);
          } else if (dia === 6 || feriado) {
            data.setDate(data.getDate() - 1);
          } else {
            data.setDate(data.getDate() - 1);
            diasUteis -= 1;
          }
        }
        const diaCalculado = data.getDay();
        if (diaCalculado === 6) data.setDate(data.getDate() - 2);
        if (diaCalculado === 0) data.setDate(data.getDate() - 1);

        const proximoDiaFeriado = this.feriadosNacionais.find(
          dataFeriado => dataFeriado.toDateString() === data.toDateString(),
        );
        if (proximoDiaFeriado) data.setDate(data.getDate() - 1);

        data.setHours(0, 0, 0, 0);
        tetoPorAbertura = data;
      }

      return tetoPorAbertura;
    },
    menorDataAbertura() {
      if (this.dados.dataPublicacao) {
        const data = new Date(this.dados.dataPublicacao);
        let diasUteis = 0;

        data.setDate(data.getDate() + 1);

        const limitBusinessDays = this.dados?.tipo === 'DISPENSA' ? 3 : 8;

        while (diasUteis < limitBusinessDays) {
          const feriado = this.feriadosNacionais.find(
            dataFeriado => dataFeriado.toDateString() === data.toDateString(),
          );
          const dia = data.getDay();
          if (dia === 0 || feriado) {
            data.setDate(data.getDate() + 1);
          } else if (dia === 6) {
            data.setDate(data.getDate() + 2);
          } else {
            data.setDate(data.getDate() + 1);
            diasUteis += 1;
          }
        }
        const diaCalculado = data.getDay();
        if (diaCalculado === 6) {
          data.setDate(data.getDate() + 2);
        }
        if (diaCalculado === 0) {
          data.setDate(data.getDate() + 1);
        }

        const proximoDiaFeriado = this.feriadosNacionais.find(
          dataFeriado => dataFeriado.toDateString() === data.toDateString(),
        );
        if (proximoDiaFeriado) {
          data.setDate(data.getDate() + 1);
        }

        return data;
      }
      return null;
    },
    menorDataFinalEntregaPropostas() {
      return this.menorDataAbertura;
    },
  },
  watch: {
    emPublicacao() {
      if (
        this.emPublicacao &&
        this.$route.name === 'PregaoEditarDados' &&
        this.getOfertaCompra.id &&
        this.dadosMontados
      ) {
        this.exibirRequeridos();
      }
    },
    // eslint-disable-next-line func-names
    'dados.modoDisputa': function (novo) {
      if (novo !== 'COM_DISPUTA') {
        this.dados.duracaoLances = null;
        if (this.$v?.dados?.duracaoLances?.$reset) this.$v.dados.duracaoLances.$reset();
      } else {
        this.ensureDuracaoValida();
      }
    },
    // eslint-disable-next-line func-names
    'dados.horaAbertura': function () {
      this.ensureDuracaoValida();
    },
  },
  methods: {
    ...mapActions([
      'buscarItens',
      'alternarEmPublicacaoPregao',
      'buscarOfertaCompra',
      'editAuction',
    ]),
    ...mapActions('purchasingUnits', ['fetchPurchasingUnitFilterOptions']),
    loadEligiblePurchasingUnits() {
      return this.fetchPurchasingUnitFilterOptions().catch(() => {});
    },
    selectPurchasingUnit(unitId) {
      const unit = this.purchasingUnitFilterOptions.find(
        option => String(option.id) === String(unitId),
      );
      if (unit && !this.purchasingUnitLocked) this.selectedPurchasingUnitId = unit.id;
    },
    subtrairDiasUteis(dataBase, qtdDiasUteis) {
      const data = new Date(dataBase);
      data.setHours(0, 0, 0, 0);

      let diasUteis = 0;
      while (diasUteis < qtdDiasUteis) {
        data.setDate(data.getDate() - 1);

        const diaSemana = data.getDay();
        const ehFimSemana = diaSemana === 0 || diaSemana === 6;
        const ehFeriado = this.feriadosNacionais.some(
          f => f.toDateString() === data.toDateString(),
        );

        if (!ehFimSemana && !ehFeriado) diasUteis += 1;
      }

      return data;
    },
    ensureDuracaoValida() {
      if (!this.showDuracaoLances) return;

      const validos = this.duracaoOptions;

      if (this.dados.duracaoLances && !validos.includes(Number(this.dados.duracaoLances))) {
        this.dados.duracaoLances = null;

        if (this.$v?.dados?.duracaoLances?.$touch) this.$v.dados.duracaoLances.$touch();
      }
    },
    horaPermiteDuracao(valorHora) {
      if (!valorHora) return true;

      const h = valorHora.getHours?.() ?? 0;
      const m = valorHora.getMinutes?.() ?? 0;

      // mesma regra de duracaoOptions: arredonda pra próxima hora se tiver minutos
      const hEfetivo = h + (m > 0 ? 1 : 0);

      // precisa haver pelo menos 6h disponível (mínimo) até 18:00
      const maxDur = Math.max(0, 18 - hEfetivo);
      return maxDur >= 6;
    },
    checkHighestDiscount(option) {
      if (Number(option) === 3) this.dados.divulgarReferencial = true;
    },
    changeRegionalPreference(value) {
      if (['AMPLO', 'AMPLOCOTA'].indexOf(value) > -1) {
        this.dados.preferenciaRegional = false;
      }
    },
    async displayPreferenceWarning() {
      if (this.dados.preferenciaRegional) {
        const confirmation = await this.$confirmacao({
          message:
            '<strong>Atenção!</strong><br><br>Você está habilitando a opção de preferência local/regional. Deseja confirmar?',
        });

        if (!confirmation) {
          this.dados.preferenciaRegional = false;
        }
      }
    },
    async exibirAvisoReferencial() {
      const maiorDesconto = this.dados.tipoNegociacaoId === 3;

      if (this.dados.divulgarReferencial && !maiorDesconto) {
        const confirmacao = await this.$confirmacao({
          message:
            '<strong>Atenção!</strong><br><br>Você está habilitando a divulgação de referenciais dos itens a partir da etapa de negociação. Deseja confirmar?',
        });

        if (!confirmacao) {
          this.dados.divulgarReferencial = false;
        }
      }
    },
    async exibirAvisoInversaoFases() {
      if (this.dados.phaseInversion) {
        const confirmacao = await this.$confirmacao({
          message:
            '<strong>Atenção!</strong><br><br>Você está habilitando a inversão de fases. Deseja confirmar?',
        });

        if (!confirmacao) {
          this.dados.phaseInversion = false;
        }
      }
    },
    async exibirAvisoManifestacaoIntencaoConsolidada() {
      if (this.dados.manifestacaoIntencaoConsolidada) {
        const confirmacao = await this.$confirmacao({
          message:
            '<strong>Atenção!</strong><br><br>Esta opção somente deverá ser utilizada quando estiver prevista no regulamento do órgão e no edital da licitação. Para órgãos sujeitos ou aderentes à <a href="https://www.gov.br/compras/pt-br/acesso-a-informacao/legislacao/instrucoes-normativas/instrucao-normativa-seges-me-no-73-de-30-de-setembro-de-2022" target="_blank" rel="noopener noreferrer">IN SEGES/ME nº 73/2022</a>, recomenda-se a manifestação após o julgamento e após a habilitação.<br><br>Deseja confirmar?',
        });

        if (!confirmacao) {
          this.dados.manifestacaoIntencaoConsolidada = false;
        }
      }
    },
    async salvarDados() {
      const dadosRequisicao = {
        ...this.dados,
        purchasingUnitId: this.selectedPurchasingUnitId,
      };

      Object.keys(dadosRequisicao).forEach(key => {
        if (dadosRequisicao[key] === '') {
          dadosRequisicao[key] = null;
        }
      });

      this.loading.salvar = true;
      try {
        await this.editAuction(dadosRequisicao);
        this.$v.dados.$reset();
        this.$alerta('Dados salvos', 'is-success');
      } catch (error) {
        const errorsItens = [
          'Todos os Item/Lote precisam ser exclusivo',
          'Selecione o(s) item(s)/lotes para Cota Reservada ou Ampla',
          'Tipo de edital não permite itens exclusivos',
        ];
        if (errorsItens.includes(error.response?.data?.error)) {
          this.$v.dados.$reset();
          this.$router.push({ name: 'PregaoEditarItens' });
          this.$alerta(error.response?.data?.error, 'is-danger');
        } else {
          this.$alerta('Erro ao salvar dados', 'is-danger');
        }
      }
      this.loading.salvar = false;
      this.alternarEmPublicacaoPregao(false);
    },
    exibirRequeridos() {
      if (this.$v.dados.$invalid) {
        Object.keys(this.$v.dados).forEach(key => {
          if (this.$v.dados[key].$invalid) {
            this.$v.dados[key].$touch();
          }
        });
      } else {
        this.$v.dados.$reset();
      }
    },
    montarOfertaCompra() {
      this.dados.tipo = this.getOfertaCompra.tipo;
      this.dados.numeroOC = this.getOfertaCompra.numero_oc;
      this.dados.numeroProcesso = this.getOfertaCompra.numero_processo;
      this.dados.objeto = this.getOfertaCompra.objeto;
      this.dados.duracaoLances = this.getOfertaCompra.duracaoLances || null;
      this.dados.origemRecursos = [...this.getOfertaCompra.origem_recursos];
      this.dados.tipoOrigemRecursos = this.getOfertaCompra.tipo_origem_recursos;
      this.dados.numeroInstrumento = this.getOfertaCompra.numero_instrumento;
      this.dados.anoInstrumento = this.getOfertaCompra.ano_instrumento;
      this.valoresMascara.anoInstrumento = this.getOfertaCompra.ano_instrumento;
      this.dados.tipoNegociacaoId = this.getOfertaCompra.tipo_negociacao
        ? this.getOfertaCompra.tipo_negociacao.id
        : null;
      this.valoresMascara.dataPublicacao = this.getOfertaCompra.data_publicacao;
      this.dados.dataPublicacao = this.getOfertaCompra.data_publicacao;
      this.valoresMascara.dataFinalEntregaPropostas =
        this.getOfertaCompra.data_final_entrega_propostas;
      this.dados.dataFinalEntregaPropostas = this.getOfertaCompra.data_final_entrega_propostas;
      this.valoresMascara.horaFinalEntregaPropostas =
        this.getOfertaCompra.data_final_entrega_propostas;
      this.dados.horaFinalEntregaPropostas = this.getOfertaCompra.data_final_entrega_propostas;
      this.valoresMascara.dataAbertura = this.getOfertaCompra.data_abertura;
      this.dados.dataAbertura = this.getOfertaCompra.data_abertura;
      this.valoresMascara.horaAbertura = this.getOfertaCompra.data_abertura;
      this.dados.horaAbertura = this.getOfertaCompra.data_abertura;
      this.dados.tipoPregao = this.getOfertaCompra.tipo_pregao;
      this.dados.modoDisputa = this.getOfertaCompra.modo_disputa;
      this.dados.tipoEdital = this.getOfertaCompra.tipo_edital;
      this.dados.preferenciaRegional = this.getOfertaCompra.preferencia_regional;
      this.dados.divulgarReferencial = this.getOfertaCompra.divulgar_referencial;
      this.dados.phaseInversion = this.getOfertaCompra.phase_inversion;
      this.dados.manifestacaoIntencaoConsolidada =
        this.getOfertaCompra.manifestacao_intencao_consolidada;
      this.dados.enderecoEntregaCEP = this.getOfertaCompra.endereco_entrega_cep;
      this.dados.enderecoEntrega = this.getOfertaCompra.endereco_entrega;
      this.dados.enderecoEntregaNumero = this.getOfertaCompra.endereco_entrega_numero;
      this.dados.enderecoEntregaComplemento = this.getOfertaCompra.endereco_entrega_complemento;
      this.dados.enderecoEntregaBairro = this.getOfertaCompra.endereco_entrega_bairro;
      this.dados.enderecoEntregaMunicipio = this.getOfertaCompra.endereco_entrega_municipio;
      this.dados.enderecoEntregaUF = this.getOfertaCompra.endereco_entrega_uf;

      this.dadosMontados = true;
    },
    getLabel() {
      return getOfertaCompraEnum(this.dados.tipo).titleSingular;
    },
    async buscarCEP() {
      if (this.dados.enderecoEntregaCEP?.trim()) {
        this.pesquisando = true;
        try {
          const {
            data: { data: response },
          } = await dadosCEP(this.dados.enderecoEntregaCEP);
          this.dados.enderecoEntrega = response.address;
          this.dados.enderecoEntregaBairro = response.neighborhood;
          this.dados.enderecoEntregaMunicipio = response.city;
          this.dados.enderecoEntregaUF = response.state;
        } catch (error) {
          this.$alerta(error.response?.data?.error || 'Erro ao buscar CEP', 'is-danger', 3000);
        }
        this.pesquisando = false;
      }
    },
    atualizarHora(campo, valor) {
      if (campo === 'horaAbertura' && this.showDuracaoLances && valor) {
        if (!this.horaPermiteDuracao(valor)) {
          this.valoresMascara.horaAbertura = null;
          this.$v.dados.horaAbertura.$model = null;
          this.$v.dados.horaAbertura.$touch();

          this.dados.duracaoLances = null;
          if (this.$v?.dados?.duracaoLances?.$reset) this.$v.dados.duracaoLances.$reset();

          this.$alerta(
            'Horário inválido: escolha um horário que permita ao menos 6h de lances.',
            'is-danger',
            4000,
          );
          return;
        }
      }

      if (this.valoresMascara[campo] !== this.dados[campo]) {
        this.atualizarDado(campo, valor);
      }

      if (campo === 'horaAbertura' && this.dados.dataAbertura && valor) {
        this.dados.dataAbertura.setHours(valor.getHours());
        this.dados.dataAbertura.setMinutes(valor.getMinutes());
      }

      if (campo === 'horaFinalEntregaPropostas' && this.dados.dataFinalEntregaPropostas && valor) {
        this.dados.dataFinalEntregaPropostas.setHours(valor.getHours());
        this.dados.dataFinalEntregaPropostas.setMinutes(valor.getMinutes());
      }

      // mantém a regra existente: se a hora mudou, revalida a duração selecionada
      this.ensureDuracaoValida();
    },
    atualizarDataPersonalizado(campo, valor) {
      if (this.valoresMascara[campo] !== this.dados[campo]) {
        if (valor) {
          const dia = valor.getDay();

          if ([0, 6].indexOf(dia) > -1) {
            this.dados[campo] = null;
            this.valoresMascara[campo] = null;
            this.$alerta('Finais de semana não são permitidos', 'is-danger', 4000);
            return;
          }

          const feriado = this.feriadosNacionais.some(
            elemento => elemento.toString() === valor.toString(),
          );
          if (feriado) {
            this.dados[campo] = null;
            this.valoresMascara[campo] = null;
            this.$alerta('Feriados não são permitidos', 'is-danger', 4000);
            return;
          }

          if (
            campo === 'dataPublicacao' &&
            this.dados.dataAbertura &&
            valor > this.maiorDataPublicacao
          ) {
            this.dados[campo] = null;
            this.valoresMascara[campo] = null;
            this.$alerta('Data de publicação fora do prazo', 'is-danger', 4000);
            return;
          }

          if (
            campo === 'dataAbertura' &&
            this.dados.dataPublicacao &&
            valor < this.menorDataAbertura
          ) {
            this.dados[campo] = null;
            this.valoresMascara[campo] = null;
            this.$alerta('Data de abertura fora do prazo', 'is-danger', 4000);
            return;
          }

          if (
            campo === 'dataFinalEntregaPropostas' &&
            this.dados.dataPublicacao &&
            valor < this.menorDataFinalEntregaPropostas
          ) {
            this.dados[campo] = null;
            this.valoresMascara[campo] = null;
            this.$alerta('Data final de entrega de propostas fora do prazo', 'is-danger', 4000);
            return;
          }

          if (campo === 'dataFinalEntregaPropostas' && this.dados.horaFinalEntregaPropostas) {
            valor.setHours(this.dados.horaFinalEntregaPropostas.getHours());
            valor.setMinutes(this.dados.horaFinalEntregaPropostas.getMinutes());
          }

          this.$v.dados[campo].$model = valor;
          if (this.dados.horaAbertura) {
            this.dados.dataAbertura.setHours(this.dados.horaAbertura.getHours());
            this.dados.dataAbertura.setMinutes(this.dados.horaAbertura.getMinutes());
          }
        }
        this.$v.dados[campo].$touch();
      }
    },
    mapearFeriadosNacionais() {
      this.feriadosNacionais = this.feriados
        .filter(filtro => filtro.type === 'public')
        .map(feriado => new Date(feriado.date));
    },
    verificarPublicacao() {
      if (this.emPublicacao) this.exibirRequeridos();
    },
  },
  mounted() {
    this.mapearFeriadosNacionais();
  },
  async created() {
    this.loading.general = true;
    try {
      await this.buscarOfertaCompra(this.$route.params.id);
      this.selectedPurchasingUnitId =
        this.getOfertaCompra?.purchasingUnitId ??
        this.getOfertaCompra?.purchasing_unit_id ??
        this.getOfertaCompra?.purchasingUnit?.id ??
        null;
      await this.buscarItens({
        ofertaCompraId: this.$route.params.id,
        listarItensLote: true,
        v2: this.$route.query.v2,
      });
      this.montarOfertaCompra();
      await this.loadEligiblePurchasingUnits();
      this.verificarPublicacao();
    } catch (error) {
      this.$alerta('Erro ao buscar dados', 'is-danger');
    } finally {
      this.loading.general = false;
    }
  },
};
</script>

<style lang="scss" scoped>
.toggles-row {
  margin-top: 4px;
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 16px 92px;

  ::v-deep > .field {
    margin-bottom: 0;
    flex-grow: 0;
    flex-shrink: 0;
  }
}

.toggle-field {
  text-align: left;
}

.auction-data-grid {
  align-items: start;
  display: grid;
  column-gap: 0.75rem;
  row-gap: 0;
  grid-template-columns: repeat(4, minmax(0, 1fr));

  ::v-deep > .field {
    min-width: 0;
  }
}

@media (min-width: 769px) and (max-width: 1439px) {
  .auction-data-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));

    ::v-deep > .field.auction-data-grid__edital {
      grid-column: 1 / -1;
      margin-bottom: 12px;
      width: 100%;
    }
  }
}

@media (max-width: 768px) {
  .auction-data-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

@media (max-width: 1023px) {
  .toggles-row {
    flex-direction: column;
    gap: 15px;
  }
}

@media (min-width: 1024px) {
  .date-time-title {
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 8px;
    color: #3d4a5d;
  }

  .date-time-row {
    margin-bottom: 2px;
  }

  .date-time-field {
    min-width: 0;
  }

  .lances-row {
    margin-top: 4px;
  }
}
</style>
